export interface EvaPagePromptContext {
  readonly applicantName?: string;
  /** Undefined until resolved, and when it cannot be resolved at all. Never assume zero. */
  readonly activePolicyCount?: number;
  /** PolicyMaster LOB code for the page's policy. Undefined means "not known", never "not auto". */
  readonly activePolicyLob?: string;
  /** Whether a future-effective renewal is waiting. Undefined means "not known", never "no renewal". */
  readonly hasPendingRenewal?: boolean;
}

export interface EvaPagePromptRule {
  /** Matched against location.pathname. SPA rules include the /web/ base; EZ4 rules do not. */
  readonly match: RegExp;
  /** Offer these only while the conversation is empty. */
  readonly newChatOnly: boolean;
  /** Ask EvaSuggestedPromptsService to resolve activePolicyCount before building. Off by default, so
   * a page that never reads the count costs no request. */
  readonly needsPolicyCount?: boolean;
  /** Resolve activePolicyLob before building. A rule setting this must capture the
   * policyMasterId as group 1 of its `match`. */
  readonly needsPolicyLob?: boolean;
  /** Resolve hasPendingRenewal before building. Same group-1 capture contract as needsPolicyLob, and
   * both are served by one policy read — so a rule setting both costs no extra request. */
  readonly needsPendingRenewal?: boolean;
  /** Static prompts to hide while this rule is active. Takes the same context as build() so a
   * conditional prompt hides its duplicate only when it actually offers a replacement. */
  excludedStaticPrompts?(context: EvaPagePromptContext): readonly string[];
  build(context: EvaPagePromptContext): string[];
}

/** The prompt wording when no applicant name can be resolved. */
const catchMeUp = 'Catch me up';
/** Duplicates the intent of the overview page's "Catch me up" prompt. */
const summarizeEmailsAndTexts = 'Summarize recent emails and texts for a client.';
/** Duplicates the intent of the policy summary page's Send ID Card prompt. */
const provideIdCardsLink = 'Provide the ID cards link for a client\'s auto policy.';

/** Policy Snapshot wording. Named so the AI engine routes it to the Policy Snapshot skill. */
const policySnapshot = (applicantName: string): string => `Show me ${applicantName}'s policy snapshot`;

/** Send ID Card wording. Named so the AI engine routes it to the Send ID Cards skill (EZ-191913). */
const sendIdCardLink = (applicantName: string): string =>
  `Send auto ID card link to ${applicantName}.`;

/**
 * Renewal Compare wording. Named so the AI engine routes it to the Renewal Compare skill.
 * Deliberately does not name the policy: it only ever appears on that policy's own summary page, so
 * the page itself is what identifies which policy is being compared.
 */
const renewalCompare = 'Compare the renewal to the current policy.';

/** LOBs with an ID card. Packages are absent by design: this reads the top-level PolicyMaster.LOB,
 * so APKGE/CPKGE/PPKGE are excluded even when they carry an auto subline. */
const idCardLobCodes: readonly string[] = ['AUTOP', 'AUTOB'];

/** Needs the name too — the prompt has to say whose card it is. Unknown LOB or name fails closed. */
const offersIdCardPrompt = ({ applicantName, activePolicyLob }: EvaPagePromptContext): boolean =>
  !!applicantName && !!activePolicyLob && idCardLobCodes.includes(activePolicyLob.toUpperCase());

/**
 * Page-specific prompts, matched in order against location.pathname — first match wins.
 * Adding a page is one entry here.
 */
export const pagePromptRules: readonly EvaPagePromptRule[] = [
  {
    // Five SPA. /web/ is environment.fiveBaseHref, identical in every build configuration.
    // Deliberately anchored to /overview: the policy routes (account/:id/policy/... and
    // account/:id/policies) cannot match, which is what keeps Policy Snapshot off the pages where a
    // specific policy is the active context. Loosening this regex would break that.
    match: /^\/web\/account\/\d+\/overview/,
    newChatOnly: true,
    needsPolicyCount: true,
    excludedStaticPrompts: () => [summarizeEmailsAndTexts],
    build: ({ applicantName, activePolicyCount }) => [
      applicantName ? `${catchMeUp} on ${applicantName}` : catchMeUp,
      // Requires the name too — the prompt has to say who is being summarized to be useful.
      // An unresolved count is not an empty one, so this stays hidden until it is known.
      ...applicantName && activePolicyCount ? [policySnapshot(applicantName)] : [],
    ],
  },
  {
    // EZ4 policy summary page url.
    match: /^\/applicantportal\/policy\/(\d+)\/summary\/index/i,
    newChatOnly: true,
    needsPolicyLob: true,
    needsPendingRenewal: true,
    excludedStaticPrompts: context => offersIdCardPrompt(context) ? [provideIdCardsLink] : [],
    // Renewal Compare leads: it tells the agent something they may not know — that a renewal landed —
    // whereas Send ID Card answers an intent they already have. Each is independently conditional,
    // and an unresolved flag is not a "no", so both fail closed rather than offer a dead prompt.
    build: context => [
      ...context.hasPendingRenewal ? [renewalCompare] : [],
      ...offersIdCardPrompt(context) ? [sendIdCardLink(context.applicantName)] : [],
    ],
  },
];

/** Generic capability hints. Offered only while the conversation is empty. */
export const staticPrompts = [
  summarizeEmailsAndTexts,
  'Show active policies and renewal dates for a client.',
  provideIdCardsLink,
];
